import { Root } from 'react-dom/client'

import { getCsrfToken } from '../../utils/api_helper'
import { initRadioGroup } from '../../components/radio_group'

import { ISchemaFormProps } from './schema_form'

// Store the current schemaform react root for unmounting
let currentRoot: Root | null = null

function showElement(element: HTMLElement | null, isShown: boolean) {
  element?.classList.toggle('d-none', !isShown)
}

function showError(
  isShown: boolean,
  text = 'Something went wrong. Please contact support.',
) {
  const element = document.querySelector<HTMLElement>('#offer-error-message')
  if (element) {
    element.textContent = text
    showElement(element, isShown)
  }
}

function showSuccess(isShown: boolean, text: string) {
  const element = document.querySelector<HTMLElement>('#offer-success-message')
  if (element) {
    element.textContent = text
    showElement(element, isShown)
  }
}

function loadFormProps(
  formDefinition: string,
  params: {
    applicationId: string | undefined
    action: string | undefined
  },
): ISchemaFormProps {
  return {
    offerResponse: params.action,
    onSave: async (payload) => {
      showError(false)
      try {
        const csrfToken = getCsrfToken()
        if (!csrfToken) {
          showError(
            true,
            'Failed to load offer form. Please refresh the page and try again. Contact support if the error persists.',
          )
          return
        }

        await fetch(
          `/partner/applications/${params.applicationId}/offer/save`,
          {
            method: 'POST',
            headers: {
              'x-csrftoken': csrfToken,
            },
            body: JSON.stringify(payload.formData),
          },
        )
        showSuccess(true, 'Form saved successfully.')
      } catch (_) {
        showError(true, 'Unable to save form. Please try again.')
      }
    },
    onSubmit: (payload) => {
      const form = document.querySelector<HTMLFormElement>('#offer-form-submit')
      const formDataElement =
        form?.querySelector<HTMLInputElement>('[name=formData]')
      const offerActionElement =
        form?.querySelector<HTMLInputElement>('[name=offerAction]')

      if (!form || !formDataElement || !offerActionElement) {
        return
      }

      formDataElement.value = JSON.stringify(payload.formData)
      offerActionElement.value = params.action ?? ''

      form.submit()

      return new Promise(() => undefined)
    },
    ...JSON.parse(formDefinition),
  }
}

async function showResponseForm(
  button: HTMLButtonElement,
  params: {
    applicationId: string | undefined
    action: string | undefined
  },
  mountElement: HTMLElement,
) {
  showError(false)

  // Reset any previous form state.
  if (currentRoot) {
    currentRoot.unmount()
    currentRoot = null
  }

  if (button.dataset.formDefinition) {
    const { configureSchemaForm } = await import('./schema_form')
    currentRoot = configureSchemaForm(
      mountElement,
      loadFormProps(button.dataset.formDefinition, params),
    )
  } else {
    const { configureSchemaForm } = await import('./schema_form')

    try {
      const formDefinition = await fetch(
        `/partner/applications/${params.applicationId}/offer?action=${params.action}`,
      ).then((response) => response.text())
      const formProps = loadFormProps(formDefinition, params)
      currentRoot = configureSchemaForm(mountElement, formProps)
      button.dataset.formDefinition = formDefinition
    } catch (_) {
      showError(true, 'Failed to load form. Please contact support.')
    }
  }
}

function configureOfferModal(
  modal: HTMLDivElement,
  schemaFormElement: HTMLDivElement,
  applicationId: string | undefined,
) {
  const offerResponseContainer = modal.querySelector<HTMLDivElement>(
    '#offer-response-form',
  )
  const loadingSpinner = modal.querySelector<HTMLDivElement>(
    '#offer-loading-spinner',
  )
  const schemaFormContainer = modal.querySelector<HTMLDivElement>(
    '#offer-schema-form-container',
  )
  const acceptanceFile = document.querySelector<HTMLDivElement>(
    '#acceptance-declaration-pdf',
  )

  if (!schemaFormElement) {
    showError(true)
    return
  }

  document
    .querySelectorAll<HTMLButtonElement>('.offer-response-button')
    .forEach((button) => {
      button.addEventListener('click', () => {
        if (button.classList.contains('continue-acceptance')) {
          const acceptanceFile = document.querySelector<HTMLDivElement>(
            '#acceptance-declaration-pdf',
          )
          showElement(acceptanceFile, true)
        }
        const params = {
          applicationId: applicationId,
          action: button.dataset.action,
        }

        showElement(offerResponseContainer, false)
        showElement(loadingSpinner, true)

        showResponseForm(button, params, schemaFormElement).then(() => {
          showElement(loadingSpinner, false)
          showElement(schemaFormContainer, true)
        })
      })
    })

  function resetModalState() {
    showElement(schemaFormContainer, false)
    showElement(acceptanceFile, false)
    showElement(offerResponseContainer, true)
    showElement(loadingSpinner, false)
    showError(false)
    showSuccess(false, '')
  }

  modal
    .querySelector<HTMLButtonElement>('#offer-back-button')
    ?.addEventListener('click', () => {
      resetModalState()
    })

  modal.addEventListener('hidden.bs.modal', () => {
    resetModalState()
  })
}

function initOfferResponseModal() {
  const legacyOfferFrame =
    document.querySelector<HTMLIFrameElement>('#offer-legacy-form')

  if (legacyOfferFrame) {
    const responseButton = document.querySelector<HTMLButtonElement>(
      '#legacy-respond-to-offer-button',
    )
    if (responseButton) {
      responseButton.addEventListener('click', (e: Event) => {
        e.preventDefault()
        import('./legacy_form').then(({ configureLegacyForm }) => {
          configureLegacyForm(
            legacyOfferFrame,
            'customer-portal-auth-',
            'spinner',
          )
        })
      })
    }
    const newDeferOfferLink = document.querySelector<HTMLAnchorElement>(
      '#new-defer-offer-link',
    )
    if (newDeferOfferLink) {
      newDeferOfferLink.addEventListener('click', (e: Event) => {
        e.preventDefault()
        import('./legacy_form').then(({ configureLegacyForm }) => {
          configureLegacyForm(
            legacyOfferFrame,
            'customer-portal-auth-defer',
            'spinner',
          )
        })
      })
    }
    const newDeclineOfferLink = document.querySelector<HTMLAnchorElement>(
      '#new-decline-offer-link',
    )
    if (newDeclineOfferLink) {
      newDeclineOfferLink.addEventListener('click', (e: Event) => {
        e.preventDefault()
        import('./legacy_form').then(({ configureLegacyForm }) => {
          configureLegacyForm(
            legacyOfferFrame,
            'customer-portal-auth-decline',
            'spinner',
          )
        })
      })
    }
    const newResponseOfferButton = document.querySelector<HTMLButtonElement>(
      '#new-respond-to-offer-button',
    )
    if (newResponseOfferButton) {
      newResponseOfferButton.addEventListener('click', (e: Event) => {
        e.preventDefault()
        import('./legacy_form').then(({ configureLegacyForm }) => {
          configureLegacyForm(
            legacyOfferFrame,
            'customer-portal-auth-accept',
            'spinner',
          )
        })
      })
    }
    const continueAcceptanceButton = document.querySelector<HTMLButtonElement>(
      '#continue-acceptance-button',
    )
    if (continueAcceptanceButton) {
      continueAcceptanceButton.addEventListener('click', (e: Event) => {
        e.preventDefault()
        import('./legacy_form').then(({ configureLegacyForm }) => {
          configureLegacyForm(
            legacyOfferFrame,
            'customer-portal-auth-accept',
            'spinner',
          )
        })
      })
    }
  }

  const offerResponseModal = document.querySelector<HTMLDivElement>(
    '#offer-response-modal',
  )
  const schemaFormElement =
    document.querySelector<HTMLDivElement>('#offer-schema-form')
  const applicationId =
    document.getElementById('application-id')?.dataset.applicationId

  if (offerResponseModal && schemaFormElement && applicationId) {
    configureOfferModal(offerResponseModal, schemaFormElement, applicationId)
  }

  const acceptanceMethodRadioGroup = document.querySelector<HTMLElement>(
    '#new-offer-response-modal .radio-group',
  )
  if (acceptanceMethodRadioGroup) {
    const sendInviteButton = document.querySelector<HTMLButtonElement>(
      '#send-invite-button',
    )
    const showAcceptanceModalButton = document.querySelector<HTMLButtonElement>(
      '#new-respond-to-offer-button',
    )
    initRadioGroup(acceptanceMethodRadioGroup, (value) => {
      const isSendInviteSelected = value === 'send_invite'
      showElement(sendInviteButton, isSendInviteSelected)
      showElement(showAcceptanceModalButton, !isSendInviteSelected)
    })
  }
}

export { initOfferResponseModal }
